/**
 * 이용권 분류 6종의 단일 정의 (CS25-8980).
 *
 * 서버 enum은 `ALL | APPOINTMENT | CLASS | PLACE | OPTION | TRANSFER_COMMISSION | ETC`이고
 * 여기서는 분류 6종만 다룬다(`ALL`은 목록 필터 전용).
 *
 * 화면군마다 서버 응답 키 이름이 다르다(매출 대시보드 / 이용권 종류별 요약 / CEO 리포트).
 * 예전에는 각 화면이 `item + "SalesAmount"`처럼 키를 조립했는데,
 * 신규 필드가 `transferCommissionAmount`·`etcAmount`라서 그 규칙이 깨진다.
 * 그래서 조립 대신 명시 매핑표를 둔다.
 */

export type ServiceTypeCode =
  | "PLACE"
  | "APPOINTMENT"
  | "CLASS"
  | "OPTION"
  | "TRANSFER_COMMISSION"
  | "ETC";

/**
 * 화면군마다 같은 분류에 다른 색을 쓴다. 통일하면 5개 화면의 색이 한꺼번에
 * 바뀌어 디자인 확인이 필요하므로, 지금 쓰이는 값을 그대로 여기 모아 두기만 했다.
 * 분류가 하나 늘 때 고칠 자리를 1곳으로 만드는 것이 목적이다.
 */
export interface ServiceTypeColors {
  /** 기본색. GlobalStyles `--{cssKey}` 변수와 홈 카드 점 */
  base: string;
  /** 짙은 색. 실시간 매출 카드의 분류 점·라벨 */
  dark: string;
  /** 리포트 홈 매출 카드 legend */
  reportLegend: string;
  /** 리포트 홈 매출 카드 도넛 조각 */
  reportDoughnut: string;
  /** 재무분석 매출 탭 항목·스택바 */
  reportAnalysis: string;
}

export interface ServiceTypeMeta {
  label: string;
  colors: ServiceTypeColors;
  /** GlobalStyles의 `--{cssKey}` 변수 / `.color .{cssKey}` 클래스명 */
  cssKey: string;
}

/** 화면 표시 순서. 분류를 순회할 때는 항상 이 배열을 쓴다. */
export const SERVICE_TYPE_ORDER: readonly ServiceTypeCode[] = [
  "PLACE",
  "APPOINTMENT",
  "CLASS",
  "OPTION",
  "TRANSFER_COMMISSION",
  "ETC",
];

export const SERVICE_TYPE_META: Record<ServiceTypeCode, ServiceTypeMeta> = {
  PLACE: {
    label: "시설이용",
    cssKey: "place",
    colors: {
      base: "#0e55e8",
      // 예전에는 실시간 대시보드가 rgb(0, 58, 972)를 썼다. 972는 0-255를
      // 벗어나 브라우저가 무시하는 값이었고, 같은 분류의 점 색(#003AAC)과도
      // 어긋났다. 점 색으로 통일했다.
      dark: "#003AAC",
      reportLegend: "#3474FA",
      reportDoughnut: "#3366FF",
      reportAnalysis: "#3b5cf0",
    },
  },
  APPOINTMENT: {
    label: "개인레슨",
    cssKey: "appointment",
    colors: {
      base: "#7a57c9",
      // 예전에는 실시간 대시보드만 #956AFF를 썼고, 점 색은 컴포넌트에서
      // 한글 라벨을 비교해 #4E23A8으로 덮어썼다. 점 색으로 통일했다.
      dark: "#4E23A8",
      reportLegend: "#7357C5",
      reportDoughnut: "#8C4FF7",
      reportAnalysis: "#8F6DD5",
    },
  },
  CLASS: {
    label: "그룹수업",
    cssKey: "class",
    colors: {
      base: "#f19825",
      dark: "#C27500",
      reportLegend: "#E79B37",
      reportDoughnut: "#FF9933",
      reportAnalysis: "#EDB85E",
    },
  },
  OPTION: {
    label: "옵션이용",
    cssKey: "option",
    colors: {
      base: "#53bbd0",
      dark: "#2E7A9F",
      reportLegend: "#53BBD0",
      reportDoughnut: "#33CCCC",
      reportAnalysis: "#58B1E3",
    },
  },
  TRANSFER_COMMISSION: {
    label: "양도 수수료",
    cssKey: "transferCommission",
    colors: {
      base: "#2db38b",
      dark: "#1F8C6B",
      reportLegend: "#2DB38B",
      reportDoughnut: "#2DB38B",
      reportAnalysis: "#2DB38B",
    },
  },
  ETC: {
    label: "기타",
    cssKey: "etc",
    colors: {
      base: "#6c727f",
      dark: "#6C727F",
      reportLegend: "#6C727F",
      reportDoughnut: "#6C727F",
      reportAnalysis: "#6C727F",
    },
  },
};

/**
 * 환불·취소. 분류가 아니므로 6종 목록/도넛 조각에 넣지 않는다.
 * 각 분류 금액이 이미 환불을 차감한 순액이라 합계에서 또 빼면 이중 차감이 된다.
 */
export const REFUND_CANCEL_META = {
  label: "환불·취소",
  color: "#fd4235",
  cssKey: "refundCancel",
};

/** `POST /sales/services/summary` · `/sales/services/branches/summary` 응답 키 */
export const VOUCHER_SUMMARY_KEYS: Record<
  ServiceTypeCode,
  { amount: string; percent: string }
> = {
  PLACE: { amount: "placeSaleAmount", percent: "placeSalePercent" },
  APPOINTMENT: {
    amount: "appointmentSalesAmount",
    percent: "appointmentSalesPercent",
  },
  CLASS: { amount: "classSalesAmount", percent: "classSalesPercent" },
  OPTION: { amount: "optionSalesAmount", percent: "optionSalesPercent" },
  TRANSFER_COMMISSION: {
    amount: "transferCommissionAmount",
    percent: "transferCommissionPercent",
  },
  ETC: { amount: "etcAmount", percent: "etcPercent" },
};

/** 이용권 종류별 요약 응답의 환불·취소 금액 키 (6분류 합계 바깥) */
export const VOUCHER_REFUND_CANCEL_KEY = "refundCancelAmount";

/**
 * 환불·취소는 서버가 **양수 크기**로 내려준다. 화면의 음수 기호는 프론트가 붙인다.
 * 서버 값을 그대로 찍으면 환불이 플러스로 보인다.
 *
 * 금액 뒤에 "원"을 붙이는 화면(홈·리포트)은 이 숫자를 그대로 포맷하면 된다.
 * 앞에 "₩"를 붙이는 화면은 `formatRefundCancelWon`을 쓴다.
 */
export const toRefundCancelAmount = (amount?: number | null): number => {
  const size = Math.abs(amount ?? 0);
  // 0이면 부호를 붙이지 않는다. -0을 그대로 내보내면
  // (-0).toLocaleString()이 "-0"이라 화면에 "-0원"이 찍힌다.
  return size === 0 ? 0 : -size;
};

/**
 * "₩" 접두 화면용 환불·취소 금액 문자열.
 *
 * 공통 `formatNumberToWonCurrency`에 음수를 넣으면 `₩-12,699,735`처럼 부호가
 * 통화 기호 뒤로 가서 읽기 나쁘다. 부호를 앞에 붙여 `-₩12,699,735`로 만든다.
 * 0이면 부호를 붙이지 않는다.
 */
export const formatRefundCancelWon = (amount?: number | null): string => {
  const size = Math.abs(amount ?? 0);
  return size === 0 ? "₩0" : `-₩${size.toLocaleString()}`;
};
